import {
  Avatar,
  Col,
  Create,
  Form,
  getValueFromEvent,
  Input,
  notification,
  Row,
  Select,
  Space,
  Upload,
  useForm,
} from '@pankod/refine-antd';
import { file2Base64, HttpError } from '@pankod/refine-core';
import avatarPlaceholder from 'assets/images/avatar-placeholder.png';
import { AddressFormItem } from 'components';
import { useUniqueValidator } from 'hooks/useUniqueValidator';
import { ISupplier, ITag } from 'interfaces';
import { useEffect, useState } from 'react';
import { checkSupplierNameUnique } from 'services/supplier';
import { getAllTag } from 'services/tag';
import {
  beforeImageUploadValidation,
  isJpgOrPngFile,
  isLessThanSize,
  MAX_UPLOAD_FILE_SIZE_MB,
} from 'utilities/file';

const { Option } = Select;

export const SupplierCreate: React.FC = () => {
  const [tags, setTags] = useState<ITag[]>([]);
  const validateSupplierName = useUniqueValidator({
    checkUnique: checkSupplierNameUnique,
    errorMessage: 'Supplier name already exists',
  });

  useEffect(() => {
    (async () => {
      try {
        const data = await getAllTag();
        const tagsData = data.data;
        setTags(tagsData);
      } catch (err) {
        if (err instanceof Error) {
          notification.error({
            message: 'Failed',
            description: err.message,
          });
        }
      }
    })();
  }, []);

  const { form, formProps, saveButtonProps, queryResult } = useForm<ISupplier, HttpError>({
    metaData: {
      fields: ['id'],
    },
  });

  const getTagOptions = () => {
    return tags.map((tag) => (
      <Option key={tag.id} value={tag.id}>
        {tag.name}
      </Option>
    ));
  };

  const onFinish = async (values: any) => {
    const { avatar, tags } = values;
    const base64Files = [];

    if (avatar && avatar.length) {
      const file = avatar[0];
      const format = file.type || '';
      const size = file.size || 0;

      if (
        isJpgOrPngFile(format, file.name || '') &&
        isLessThanSize(size, MAX_UPLOAD_FILE_SIZE_MB)
      ) {
        if (file.originFileObj) {
          const base64String = await file2Base64(file);
          const { originFileObj, ...rest } = file;
          base64Files.push({ ...rest, base64String });
        } else {
          base64Files.push(file);
        }
      }
    }

    const tagsPayload = !!tags ? tags.map((tag: string) => ({ id: tag })) : [];

    formProps.onFinish &&
      formProps.onFinish({
        ...values,
        tags: tagsPayload,
        avatar: base64Files,
      });
  };

  return (
    <Create isLoading={queryResult?.isFetching} saveButtonProps={saveButtonProps}>
      <Form
        {...formProps}
        form={form}
        layout="vertical"
        initialValues={{
          isActive: true,
        }}
        scrollToFirstError
        onFinish={onFinish}
      >
        <Row gutter={[56, 0]} wrap>
          <Col xs={24} lg={8}>
            <Space
              direction="vertical"
              style={{
                width: '100%',
                textAlign: 'center',
                marginBottom: '16px',
                marginTop: '6px',
              }}
            >
              <Form.Item
                name="avatar"
                valuePropName="fileList"
                getValueFromEvent={getValueFromEvent}
                noStyle
              >
                <Upload.Dragger
                  listType="picture"
                  accept="image/png, image/jpeg, image/heic, image/heif, .heic, .heif"
                  maxCount={1}
                  beforeUpload={beforeImageUploadValidation}
                >
                  <Avatar
                    size={200}
                    style={{ objectFit: 'cover' }}
                    src={avatarPlaceholder}
                    alt="Store Location"
                  />
                  <p className="ant-upload-text">Drag & drop an image file in this area</p>
                </Upload.Dragger>
              </Form.Item>
            </Space>
          </Col>

          <Col xs={24} lg={12}>
            <Form.Item
              label="Supplier Name"
              name="name"
              validateTrigger="onChange"
              hasFeedback
              rules={[
                { required: true, message: 'Supplier name is required' },
                { validator: validateSupplierName },
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              label="Email"
              name="email"
              rules={[
                {
                  type: 'email',
                },
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              label="Mobile Number"
              name="mobileNumber"
              rules={[
                {
                  pattern: /^(\+\d{1,3}[- ]?)?\d{8,12}$/,
                  message: 'Provide valid number',
                },
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              label="Unique ID"
              name="supplierCode"
              extra="This ID will be automatically generated after saving."
            >
              <Input />
            </Form.Item>
            <Form.Item label="Tags" name="tags">
              <Select
                showSearch
                mode="multiple"
                style={{ width: '100%' }}
                placeholder="Select tags"
                filterOption={(input, option: any) =>
                  option!.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                {getTagOptions()}
              </Select>
            </Form.Item>
            <AddressFormItem />
          </Col>
        </Row>
      </Form>
    </Create>
  );
};
